import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, XCircle, Clock, Eye, Filter, Search, Calendar, User, BookOpen } from 'lucide-react';
import respostasService from '../services/respostasService';
import { getCurrentUserProfile } from '../services/usuariosService';
import '../styles/respostas.css';

const Respostas = () => {
  const [testesRealizados, setTestesRealizados] = useState([]);
  const [estatisticas, setEstatisticas] = useState({
    total: 0,
    aprovados: 0,
    reprovados: 0,
    mediaPercentual: 0
  });
  const [loading, setLoading] = useState(true);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [filtros, setFiltros] = useState({
    busca: '',
    aprovado: '',
    dataInicio: '',
    dataFim: ''
  });
  const [testeDetalhado, setTesteDetalhado] = useState(null);
  const [modalDetalhes, setModalDetalhes] = useState(false);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      
      // Carregar perfil do usuário logado
      const profileResult = await getCurrentUserProfile();
      if (profileResult.success) {
        setCurrentUserProfile(profileResult.data);
      }
      
      const [testesData, estatisticasData] = await Promise.all([
        respostasService.getTestesRealizados(),
        respostasService.getEstatisticas()
      ]);
      
      setTestesRealizados(testesData);
      setEstatisticas(estatisticasData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    return testesRealizados.filter(teste => {
      // Se for school admin, filtrar apenas alunos da escola do usuário
      if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
        if (teste.alunos.escola_id !== currentUserProfile.school_id) {
          return false;
        }
      }
      
      const matchBusca = !filtros.busca || 
        teste.alunos.nome.toLowerCase().includes(filtros.busca.toLowerCase()) ||
        teste.testes.nome.toLowerCase().includes(filtros.busca.toLowerCase());
      
      const matchAprovado = filtros.aprovado === '' || 
        (filtros.aprovado === 'true' && teste.status_aprovacao === 'aprovado') ||
        (filtros.aprovado === 'false' && teste.status_aprovacao === 'reprovado');
      
      const matchDataInicio = !filtros.dataInicio || 
        new Date(teste.data_fim) >= new Date(filtros.dataInicio);
      
      const matchDataFim = !filtros.dataFim || 
        new Date(teste.data_fim) <= new Date(filtros.dataFim);
      
      return matchBusca && matchAprovado && matchDataInicio && matchDataFim;
    });
  };

  const visualizarDetalhes = async (execucaoId) => {
    try {
      const detalhes = await respostasService.getDetalhesTeste(execucaoId);
      setTesteDetalhado(detalhes);
      setModalDetalhes(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
    }
  };

  const testesFiltrados = aplicarFiltros();

  return (
    <div className="respostas">
      <div className="page-header">
        <h1 className="page-title">Respostas</h1>
        <p className="page-subtitle">Acompanhamento de respostas dos testes</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <FileText size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? testesRealizados.filter(t => t.alunos.escola_id === currentUserProfile.school_id).length
              : estatisticas.total
            }
          </div>
          <div className="stat-label">Total de Testes</div>
        </div>
        <div className="stat-card success">
          <div className="stat-header">
            <div className="stat-icon success">
              <CheckCircle size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? testesRealizados.filter(t => t.alunos.escola_id === currentUserProfile.school_id && t.status_aprovacao === 'aprovado').length
              : estatisticas.aprovados
            }
          </div>
          <div className="stat-label">Aprovados</div>
        </div>
        <div className="stat-card danger">
          <div className="stat-header">
            <div className="stat-icon danger">
              <XCircle size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? testesRealizados.filter(t => t.alunos.escola_id === currentUserProfile.school_id && t.status_aprovacao === 'reprovado').length
              : estatisticas.reprovados
            }
          </div>
          <div className="stat-label">Reprovados</div>
        </div>
        <div className="stat-card info">
          <div className="stat-header">
            <div className="stat-icon info">
              <BookOpen size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? (() => {
                  const testesEscola = testesRealizados.filter(t => t.alunos.escola_id === currentUserProfile.school_id);
                  const media = testesEscola.length > 0 
                    ? testesEscola.reduce((acc, t) => acc + (t.percentual_acerto || 0), 0) / testesEscola.length 
                    : 0;
                  return Math.round(media * 10) / 10;
                })()
              : estatisticas.mediaPercentual
            }%
          </div>
          <div className="stat-label">Média Geral</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="content-card">
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <Search size={16} />
              <input
                type="text"
                placeholder="Buscar por aluno ou teste..."
                value={filtros.busca}
                onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                className="filter-input"
              />
            </div>
            
            <div className="filter-group">
              <Filter size={16} />
              <select
                value={filtros.aprovado}
                onChange={(e) => setFiltros({...filtros, aprovado: e.target.value})}
                className="filter-select"
              >
                <option value="">Todos os resultados</option>
                <option value="true">Aprovados</option>
                <option value="false">Reprovados</option>
              </select>
            </div>
            
            <div className="filter-group">
              <Calendar size={16} />
              <input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
                className="filter-input"
                placeholder="Data início"
              />
            </div>
            
            <div className="filter-group">
              <Calendar size={16} />
              <input
                type="date"
                value={filtros.dataFim}
                onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
                className="filter-input"
                placeholder="Data fim"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Resultados */}
      <div className="content-card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Carregando testes realizados...</p>
          </div>
        ) : (
          <>
            <div className="table-header">
              <h3>Testes Realizados ({testesFiltrados.length})</h3>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Aluno</th>
                    <th>Teste</th>
                    <th>Matéria</th>
                    <th>Acertos</th>
                    <th>Percentual</th>
                    <th>Pontuação</th>
                    <th>Status</th>
                    <th>Data/Hora</th>
                    <th>Tempo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {testesFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="no-data">
                        Nenhum teste encontrado com os filtros aplicados
                      </td>
                    </tr>
                  ) : (
                    testesFiltrados.map(teste => (
                      <tr key={teste.id}>
                        <td>
                          <div className="user-info">
                            <User size={16} />
                            <span>{teste.alunos.nome}</span>
                          </div>
                        </td>
                        <td className="test-name">{teste.testes.nome}</td>
                        <td className="subject">{teste.testes.materias?.nome || '-'}</td>
                        <td className="score-fraction">
                          {teste.questoes_corretas || 0}/{teste.questoes_total || 0}
                        </td>
                        <td>
                          <div className="percentage-badge">
                            {teste.percentual_acerto || 0}%
                          </div>
                        </td>
                        <td className="points">
                          {teste.pontuacao_obtida || 0}/{teste.testes.pontuacao_maxima || 0}
                        </td>
                        <td>
                          <span className={`status-badge ${teste.status_aprovacao === 'aprovado' ? 'approved' : 'failed'}`}>
                            {teste.status_aprovacao === 'aprovado' ? 'Aprovado' : 'Reprovado'}
                          </span>
                        </td>
                        <td className="date-time">
                          {respostasService.formatarData(teste.data_fim)}
                        </td>
                        <td className="duration">
                          {respostasService.formatarTempo(teste.duracao_minutos)}
                        </td>
                        <td>
                          <button
                            onClick={() => visualizarDetalhes(teste.id)}
                            className="action-btn view"
                            title="Ver detalhes"
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal de Detalhes */}
      {modalDetalhes && testeDetalhado && (
        <div className="modal-overlay" onClick={() => setModalDetalhes(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalhes do Teste</h2>
              <button 
                onClick={() => setModalDetalhes(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {/* Informações Gerais */}
              <div className="test-details-header">
                <div className="detail-group">
                  <label>Aluno:</label>
                  <span>{testeDetalhado.execucao.alunos.nome}</span>
                </div>
                <div className="detail-group">
                  <label>Teste:</label>
                  <span>{testeDetalhado.execucao.testes.nome}</span>
                </div>
                <div className="detail-group">
                  <label>Matéria:</label>
                  <span>{testeDetalhado.execucao.testes.materias?.nome || '-'}</span>
                </div>
                <div className="detail-group">
                  <label>Resultado:</label>
                  <span className={`status-badge ${testeDetalhado.execucao.status_aprovacao === 'aprovado' ? 'approved' : 'failed'}`}>
                    {testeDetalhado.execucao.status_aprovacao === 'aprovado' ? 'Aprovado' : 'Reprovado'}
                  </span>
                </div>
              </div>

              {/* Estatísticas */}
              <div className="test-stats">
                <div className="stat-item">
                  <label>Acertos:</label>
                  <span>{testeDetalhado.execucao.questoes_corretas}/{testeDetalhado.execucao.questoes_total}</span>
                </div>
                <div className="stat-item">
                  <label>Percentual:</label>
                  <span>{testeDetalhado.execucao.percentual_acerto}%</span>
                </div>
                <div className="stat-item">
                  <label>Pontuação:</label>
                  <span>{testeDetalhado.execucao.pontuacao_obtida}/{testeDetalhado.execucao.testes.pontuacao_maxima}</span>
                </div>
                <div className="stat-item">
                  <label>Tempo:</label>
                  <span>{respostasService.formatarTempo(testeDetalhado.execucao.duracao_minutos)}</span>
                </div>
              </div>

              {/* Questões e Respostas */}
              <div className="questions-section">
                <h3>Questões e Respostas</h3>
                <div className="questions-list">
                  {testeDetalhado.questoes.map((questao, index) => {
                    const respostaAluno = testeDetalhado.execucao.respostas?.[questao.id];
                    const isCorrect = questao.tipo_questao === 'dissertativa' 
                      ? respostaAluno?.trim().length > 0
                      : questao.tipo_questao === 'multipla_escolha'
                      ? questao.alternativas?.[parseInt(questao.resposta_correta)] === respostaAluno
                      : respostaAluno === questao.resposta_correta;
                    
                    return (
                      <div key={questao.id} className={`question-item ${isCorrect ? 'correct' : 'incorrect'}`}>
                        <div className="question-header">
                          <span className="question-number">Questão {index + 1}</span>
                          <span className={`result-icon ${isCorrect ? 'correct' : 'incorrect'}`}>
                            {isCorrect ? <CheckCircle size={16} /> : <XCircle size={16} />}
                          </span>
                        </div>
                        
                        <div className="question-text">
                          {questao.questao}
                        </div>
                        
                        {questao.tipo_questao === 'multipla_escolha' && questao.alternativas && (
                          <div className="alternatives">
                            {questao.alternativas.map((alt, altIndex) => (
                              <div 
                                key={altIndex} 
                                className={`alternative ${
                                  altIndex === parseInt(questao.resposta_correta) ? 'correct' : ''
                                } ${
                                  alt === respostaAluno ? 'selected' : ''
                                }`}
                              >
                                <span className="alt-letter">{String.fromCharCode(65 + altIndex)}</span>
                                <span>{alt}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="answer-section">
                          <div className="student-answer">
                            <label>Resposta do aluno:</label>
                            <span>{respostaAluno || 'Não respondida'}</span>
                          </div>
                          
                          {questao.tipo_questao !== 'dissertativa' && (
                            <div className="correct-answer">
                              <label>Resposta correta:</label>
                              <span>
                                {questao.tipo_questao === 'multipla_escolha' 
                                  ? questao.alternativas?.[parseInt(questao.resposta_correta)] || questao.resposta_correta
                                  : questao.resposta_correta
                                }
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Respostas;
